/**
 * Renders the "Community Questions" page for a given topic, which simply
 * aggregates recent questions from videos and articles within that topic.
 */

import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import {compose} from "mu-lambda";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import Link from "@khanacademy/wonder-blocks-link";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {Body} from "@khanacademy/wonder-blocks-typography";
import {
    enums,
    withConversionContext,
    withMarkConversion,
    type PartialLearningTimeInfo,
    type WithMarkConversionProps,
    type WithoutMarkConversion,
} from "@khan/analytics-learning-time";
import {PageloadMarker} from "@khan/analytics-performance";
import {mediaQueries} from "@khan/shared-styles";

// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedBreadcrumbs from "#components/breadcrumbs-package/deprecated-breadcrumbs.tsx";
import type {ApolloGqlClient} from "#data-access/gql/apollo-types.ts";
import withApollo, {
    type WithApolloProps,
    type WithoutApollo,
} from "#data-access/gql/with-apollo.tsx";
import {
    defaultApi,
    type DiscussionApi,
    type FetchPosts_Parameters,
    type GqlEndpoint,
} from "#discussion-package/components/api.ts";
import StatefulDiscussionList from "#discussion-package/components/stateful-discussion-list.tsx";
import {GET_TOPIC_QUESTIONS_QUERY} from "#discussion-package/graphql/topic_questions_query.ts";
import type {
    DiscussionsServerResponse,
    FeedbackType,
} from "#discussion-package/types.ts";
import {filterFeedbackList} from "#discussion-package/util/feedback-filters.ts";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import {deprecatedTypography} from "#shared-styles-package/deprecated-typography.ts";
import {getZendeskCommunityLink} from "#zendesk/zendesk-util.ts";

import type {CourseInfo, DiscussionData} from "./types.ts";

export function Guidelines(): React.ReactElement {
    return (
        <Body style={styles.guidelines}>
            <h2 className="sr-only">{t`Guidelines`}</h2>
            <h3 className={css(styles.guidelines_h3)}>
                {t`Give a great answer`}
            </h3>
            <h4 className={css(styles.guidelines_h4)}>{t`Be a guide`}</h4>
            {t`We're teaching each other – so don't just answer "Yes" or "No", share your answer the way your favorite teacher would.`}
            <h4 className={css(styles.guidelines_h4)}>{t`Vote, don't echo`}</h4>
            {t`If someone has already answered the question well, don't repeat their answer – vote it up instead.`}
            <h3 className={css(styles.guidelines_h3)}>
                {t`Flag inappropriate posts`}
            </h3>
            {t`Here are posts to avoid making. If you do encounter them, flag them for attention from our Guardians.`}
            <h4 className={css(styles.guidelines_h4)}>{t`abuse`}</h4>
            <ul className={css(styles.guidelines_ul)}>
                <li>{t`disrespectful or offensive`}</li>
                <li>{t`an advertisement`}</li>
            </ul>
            <h4 className={css(styles.guidelines_h4)}>{t`not helpful`}</h4>
            <ul className={css(styles.guidelines_ul)}>
                {}
                <li>
                    {t({
                        context: "A discussion post that is of low quality",
                        message: "low quality",
                    })}
                </li>
                {/* I18N: A discussion post that is unrelated to the video */}
                <li>
                    {t({
                        context: "A discussion post that is of low quality",
                        message: "not about the video topic",
                    })}
                </li>
                {/* I18N: A discussion post that is about a contentious topic
                    such as politics, religion/atheism, or personal issues */}
                <li>
                    {t({
                        context: "A discussion post that is of low quality",
                        message:
                            "contentious posts about politics, religion/atheism, or personal issues",
                    })}
                </li>
                {/* I18N: A discussion post that solicits votes or badges */}
                <li>
                    {t({
                        context: "A discussion post that is of low quality",
                        message: "soliciting votes or seeking badges",
                    })}
                </li>
                {/* I18N: A discussion post that asks for an answer to a
                    homework question */}
                <li>
                    {t({
                        context: "A discussion post that is of low quality",
                        message: "a homework question",
                    })}
                </li>
                {/* I18N: A discussion post that duplicates another post */}
                <li>
                    {t({
                        context: "A discussion post that is of low quality",
                        message: "a duplicate answer",
                    })}
                </li>
                {/* I18N: A discussion post that is posted numerous times */}
                <li>
                    {t({
                        context: "A discussion post that is of low quality",
                        message: "repeatedly making the same post",
                    })}
                </li>
            </ul>
            <h4 className={css(styles.guidelines_h4)}>{t`wrong category`}</h4>
            <ul className={css(styles.guidelines_ul)}>
                <li>{t`a tip or thanks in Questions`}</li>
                <li>{t`an answer that should be its own question`}</li>
            </ul>
            <h4 className={css(styles.guidelines_h4)}>{t`about the site`}</h4>
            <ul className={css(styles.guidelines_ul)}>
                <li>
                    {t`a question about Khan Academy`}
                    <div>
                        (
                        <Link href="https://khanacademy.zendesk.com/">
                            {t`Visit our help center`}
                        </Link>
                        )
                    </div>
                </li>
                <li>{t`a post about badges`}</li>
                <li>
                    {t`a technical problem with the site`}
                    <div>
                        (
                        <Link href="https://support.khanacademy.org/hc/requests/new">
                            {t`Report a problem`}
                        </Link>
                        )
                    </div>
                </li>
                <li>
                    <Link href={`${getZendeskCommunityLink()}/200136634`}>
                        {t`a request for features`}
                    </Link>
                </li>
            </ul>
        </Body>
    );
}

type OwnProps = {
    discussion: {
        data: DiscussionData;
        questionType: FeedbackType;
    };
    // Used for learning time conversion.
    domainId: string | null | undefined;
    topic: CourseInfo;
};

type Props = OwnProps & WithMarkConversionProps & WithApolloProps;

class CourseDiscussion extends React.Component<Props> {
    componentDidMount() {
        const {markConversion} = this.props;
        markConversion({id: "pageview_course_discussion"});
    }
    getAPI(): DiscussionApi {
        const {topic} = this.props;

        // We override fetchPosts to replace feedbackQuery with topicQuestions so that we
        // can return all feedback within a topic of a feedbackType (eg. questions in algebra)
        const fetchPosts: GqlEndpoint<
            FetchPosts_Parameters,
            ApolloGqlClient,
            DiscussionsServerResponse | null | undefined
        > = async (params: FetchPosts_Parameters) => {
            const queryParams: any = {
                limit: 10,
                feedbackType: params.feedbackType,
                topicId: topic.id,
            };

            if (params.cursor) {
                queryParams.cursor = params.cursor;
            }

            const {gqlClient} = this.props;

            const response = await gqlClient.query(GET_TOPIC_QUESTIONS_QUERY, {
                variables: queryParams,
                fetchPolicy: "network-only",
            });
            if (
                response &&
                response.data &&
                response.data.topicQuestions?.feedback
            ) {
                const cursor = response?.data?.topicQuestions?.cursor ?? "";

                const isComplete =
                    response?.data?.topicQuestions?.complete ?? false;
                const feedback = response.data.topicQuestions.feedback ?? [];
                const filteredFeedback = filterFeedbackList(feedback);

                return {
                    cursor: cursor,
                    feedback: filteredFeedback,
                    isComplete: isComplete,
                    qaExpandKey: params.qaExpandKey || "",
                    sortedByDate: true,
                    totalCount: filteredFeedback.length,
                    type: params.feedbackType,
                };
            }
            return null;
        };

        return {
            ...defaultApi,
            getId() {
                return "COURSE_DISCUSSION";
            },
            fetchPosts,
        };
    }

    renderDiscussion: () => React.ReactNode = () => {
        const {discussion} = this.props;

        const data = discussion.data;
        const topicSlug = (data.topic && data.topic.slug) || "";
        const isComplete = !data.cursor;

        return (
            <View style={styles.discussionListContainer}>
                <StatefulDiscussionList
                    tab={discussion.questionType}
                    topicSlug={topicSlug}
                    posts={data.feedback}
                    isComplete={isComplete}
                    discussionContext={{}}
                    cursor={data.cursor}
                    apiOverrides={this.getAPI()}
                />
            </View>
        );
    };

    render(): React.ReactNode {
        const {discussion, topic} = this.props;

        const breadcrumbs = [
            {
                title: topic.title,
                href: topic.url,
            },
        ];

        let headerTitle = t`Questions`;
        let discussionTitle = t`Recent questions`;

        if (discussion.questionType === "comments") {
            headerTitle = t`Tips and Thanks`;
            discussionTitle = t`Recent tips and thanks`;
        }

        const headerSection = (
            <div>
                <div className={css(styles.headerWithBreadcrumbs)}>
                    <div className={css(styles.breadcrumbs)}>
                        <DeprecatedBreadcrumbs
                            isNavigation={true}
                            path={breadcrumbs}
                            separator="|"
                            theme="dark"
                        />
                    </div>
                    <h1 className={css(styles.title)}>
                        {`${topic.title}: `}
                        {headerTitle}
                    </h1>
                </div>
                {discussion.questionType === "questions" && <Guidelines />}
            </div>
        );

        const discussionSection = (
            <div>
                <div className={css(styles.discussionTip)}>
                    {t`Visit a video or article to ask a question.`}
                </div>
                <h2 className={css(styles.discussionTitle)}>
                    {discussionTitle}
                </h2>
                {this.renderDiscussion()}
            </div>
        );

        return (
            <div className={css(styles.wrapper)}>
                <div className={css(styles.headerOuter)}>
                    <div className={css(styles.headerMiddle)}>
                        <div className={css(styles.headerInner)}>
                            {headerSection}
                        </div>
                    </div>
                </div>
                <div className={css(styles.discussionOuter)}>
                    <div className={css(styles.discussionMiddle)}>
                        <div className={css(styles.discussionInner)}>
                            {discussionSection}
                        </div>
                    </div>
                </div>
                <div style={{clear: "both"}} />
                <PageloadMarker key={topic.id} pageName="discussion_page" />
            </div>
        );
    }
}

const styles = StyleSheet.create({
    wrapper: {
        color: color.offBlack,
        backgroundColor: color.offWhite,

        [mediaQueries.mdOrLarger]: {
            float: "left",
            width: "100%",
        },
    },

    headerOuter: {
        [mediaQueries.smOrSmaller]: {
            // At phone width, mimic the curation page header
            boxSizing: "border-box",
        },
        [mediaQueries.mdOrLarger]: {
            // At tablet/desktop widths, use the old side-by-side design
            float: "left",
            width: "50%",
        },
    },
    discussionOuter: {
        [mediaQueries.mdOrLarger]: {
            float: "right",
            width: "50%",
        },
    },

    headerMiddle: {
        [mediaQueries.mdOrLarger]: {
            marginRight: 200,
            paddingLeft: 20,
            paddingRight: 40,
        },
    },
    discussionMiddle: {
        background: color.white,

        [mediaQueries.mdOrLarger]: {
            marginLeft: -200,
            paddingLeft: 40,
            paddingRight: 20,
        },
    },

    headerInner: {
        // Disable subpixel antialiasing for OSX (improves light on dark text)
        MozOsxFontSmoothing: "grayscale",
        WebkitFontSmoothing: "antialiased",

        [mediaQueries.smOrSmaller]: {
            alignItems: "center",
            display: "flex",
            height: 134,
            justifyContent: "center",
            textAlign: "center",
        },
        [mediaQueries.mdOrLarger]: {
            marginLeft: "auto",
            maxWidth: 340,
            paddingTop: 40,
        },
    },
    discussionInner: {
        [mediaQueries.smOrSmaller]: {
            marginLeft: "auto",
            marginRight: "auto",
            maxWidth: 576,
            minHeight: 100,
            paddingLeft: 16,
            paddingRight: 16,
            paddingTop: 40,
        },
        [mediaQueries.md]: {
            // Ensure that white background extends to bottom of page
            minHeight: 2140,
        },
        [mediaQueries.mdOrLarger]: {
            marginRight: "auto",
            maxWidth: 740,
            paddingTop: 40,
        },
        [mediaQueries.lgOrLarger]: {
            // Ensure that white background extends to bottom of page
            minHeight: 1320,
        },
    },

    headerWithBreadcrumbs: {
        position: "relative",
        paddingBottom: 3,
    },
    title: {
        marginBottom: 0,

        [mediaQueries.mdOrSmaller]: {
            ...deprecatedTypography.conceptHeadingMobile,
        },
        [mediaQueries.lgOrLarger]: {
            // Note that header sizes increase at lg breakpoint, not md
            ...deprecatedTypography.conceptHeadingDesktop,
        },
    },
    // eslint-disable-next-line react-native/no-unused-styles
    desktopBreadcrumbsBackArrow: {
        [mediaQueries.mdOrSmaller]: {
            display: "none",
        },
        [mediaQueries.lgOrLarger]: {
            position: "absolute",
            left: -12,
            top: 0,
        },
    },
    // eslint-disable-next-line react-native/no-unused-styles
    mobileBreadcrumbsBackArrow: {
        [mediaQueries.smOrSmaller]: {
            marginRight: 1,
        },
        [mediaQueries.md]: {
            position: "absolute",
            left: -11,
            top: 0,
        },
        [mediaQueries.lgOrLarger]: {
            display: "none",
        },
    },
    breadcrumbs: {
        // Note that header sizes increase at lg breakpoint, not md
        paddingBottom: 4,

        [mediaQueries.mdOrSmaller]: {
            paddingBottom: 8,
        },
    },
    guidelines: {
        [mediaQueries.smOrSmaller]: {
            display: "none",
        },
    },
    guidelines_h3: {
        ...deprecatedTypography.bodyLarge,
        borderBottom: `1px solid ${color.offBlack16}`,
        marginTop: 44,
    },
    guidelines_h4: {
        ...deprecatedTypography.bodySmall,
        marginBottom: 11,
        marginTop: 22,
    },
    guidelines_ul: {
        listStyleType: "disc",
        paddingLeft: 30,
    },
    discussionTip: {
        [mediaQueries.smOrSmaller]: {
            display: "none",
        },
        [mediaQueries.mdOrLarger]: {
            float: "right",
        },
        [mediaQueries.lgOrLarger]: {
            // Note that header sizes increase at lg breakpoint, not md
            ...deprecatedTypography.bodyXsmall,
        },
    },
    discussionTitle: {
        borderBottom: `1px solid ${color.offBlack16}`,
        color: color.offBlack,
        marginBottom: 10,
        paddingBottom: 10,

        [mediaQueries.mdOrSmaller]: {
            ...deprecatedTypography.subheadingMobile,
        },
        [mediaQueries.mdOrLarger]: {
            marginLeft: "auto",
            marginRight: "auto",
            maxWidth: "auto",
        },
        [mediaQueries.lgOrLarger]: {
            // Note that header sizes increase at lg breakpoint, not md
            ...deprecatedTypography.subheadingDesktop,
        },
    },
    discussionListContainer: {
        paddingBottom: spacing.xLarge_32,
    },
});

const mapPropsToLearningTime = (
    ownProps: OwnProps,
): PartialLearningTimeInfo => {
    const {domainId, topic} = ownProps;
    return {
        activity: enums.Activity.DISCUSSING,
        product: enums.Product.OTHER_PRODUCT,
        content: {
            kind: enums.ContentKind.Topic,
            domainId: domainId || "",
            // Here, `ownProps.topic` is a Course (formerly Subject).
            courseId: topic.id,
        },
    };
};

type ExportProps = WithoutMarkConversion<
    WithoutApollo<
        JSX.LibraryManagedAttributes<
            typeof CourseDiscussion,
            React.ComponentProps<typeof CourseDiscussion>
        >
    >
>;

export default compose(
    withConversionContext(mapPropsToLearningTime),
    withMarkConversion,
    withApollo,
)(CourseDiscussion) as React.ComponentType<ExportProps>;
